// Slice 3a — IntelliSense "preceding-expression operand" detector.
//
// Sibling to `findPrecedingColumnOperand`: when the token immediately left of
// the cursor is a *completed expression* (a `)`-terminated function call /
// CAST / CONVERT, a PostgreSQL `expr::type` cast, or top-level arithmetic /
// concatenation) sitting at an operand boundary (after WHERE / AND / OR / ON /
// comma / paren), infer the expression's coarse result type so the resolver
// can prune type-invalid operators — exactly as it does for typed columns.
//
// Returns the inferred `OperandLiteralSqlType`, or null when there's no
// completed expression at the cursor, it isn't at an operand boundary, or the
// type can't be inferred with confidence (→ caller suppresses nothing).

import type { DatabaseEngineId } from '@/core/engines/engineTypes';
import type { SqlEngine } from '@/features/sql-grammar/model';
import {
  inferExpressionResultType,
  type ResolveRoutineReturnType,
} from '@/features/sql-resolver/structure/inferExpressionResultType';
import type { OperandLiteralSqlType } from '@/features/sql-resolver/types';

import {
  isAtOperandBoundary,
  isWhitespaceChar,
  readBackwardIdentifier,
} from './findPrecedingColumnOperand';

function toSqlEngine(engine: DatabaseEngineId): SqlEngine {
  return engine === 'pgsql' ? 'postgres' : (engine as SqlEngine);
}

export type FindExpressionArgs = {
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  readonly engine: DatabaseEngineId;
  // Optional (A3): types a single user-defined-function call by its declared
  // return type. Built via `makeRoutineReturnResolver(args.snapshotRoutines)`.
  readonly resolveRoutineReturnType?: ResolveRoutineReturnType;
};

export function findPrecedingExpressionType(
  args: FindExpressionArgs,
): OperandLiteralSqlType | null {
  const { text, offset, statementStart } = args;
  let pos = offset;
  while (pos > statementStart && isWhitespaceChar(text.charCodeAt(pos - 1))) pos -= 1;
  if (pos === statementStart) return null;

  const exprStart = findExpressionStart(text, pos, statementStart);
  if (exprStart === null || exprStart >= pos) return null;
  if (!isAtOperandBoundary(text, exprStart, statementStart)) return null;

  const exprText = text.slice(exprStart, pos);
  const result = inferExpressionResultType(
    exprText,
    toSqlEngine(args.engine),
    args.resolveRoutineReturnType,
  );
  return result === 'unknown' ? null : result;
}

/**
 * Walk back from `end` over `atom ( connector atom )*`, where an atom is a
 * `name( … )` call, a dotted identifier chain, or a numeric literal, and a
 * connector is `::` (cast) or a top-level `*` / `/` / `%` / `||`. Returns the
 * start offset of the leftmost atom (the whole expression's start), or null.
 * Each iteration moves strictly left, so the loop terminates.
 */
function findExpressionStart(text: string, end: number, limit: number): number | null {
  let start = readAtomStart(text, end, limit);
  if (start === null) return null;
  for (;;) {
    let j = start;
    while (j > limit && isWhitespaceChar(text.charCodeAt(j - 1))) j -= 1;
    const connectorEnd = readConnectorBefore(text, j, limit);
    if (connectorEnd === null) return start;
    let leftEnd = connectorEnd;
    while (leftEnd > limit && isWhitespaceChar(text.charCodeAt(leftEnd - 1))) leftEnd -= 1;
    const leftStart = readAtomStart(text, leftEnd, limit);
    if (leftStart === null || leftStart >= start) return start;
    start = leftStart;
  }
}

/** Start offset of one atom ending (exclusively) at `end`, or null. */
function readAtomStart(text: string, end: number, limit: number): number | null {
  if (end <= limit) return null;
  const last = text.charCodeAt(end - 1);
  if (last === 0x29 /* ) */) return readBackwardCallStart(text, end, limit);
  if (isNumberChar(last)) return readBackwardNumber(text, end, limit);
  return readBackwardDottedRefStart(text, end, limit);
}

/**
 * If a connector (`::`, `*`, `/`, `%`, `||`) ends exactly at `j`, return the
 * offset just before it (where the left operand ends); else null. `+` / `-`
 * are excluded — `inferExpressionResultType` can't type them (date math /
 * MSSQL concat), so bounding across them would only ever yield 'unknown'.
 */
function readConnectorBefore(text: string, j: number, limit: number): number | null {
  if (j - 2 >= limit && text.charCodeAt(j - 1) === 0x3a && text.charCodeAt(j - 2) === 0x3a) {
    return j - 2; // ::
  }
  const c = text.charCodeAt(j - 1);
  if (c === 0x2a /* * */ || c === 0x2f /* / */ || c === 0x25 /* % */) return j - 1;
  if (c === 0x7c /* | */ && j - 2 >= limit && text.charCodeAt(j - 2) === 0x7c) return j - 2; // ||
  return null;
}

function isNumberChar(code: number): boolean {
  return (code >= 0x30 && code <= 0x39) || code === 0x2e /* . */;
}

function readBackwardNumber(text: string, end: number, limit: number): number | null {
  let start = end;
  while (start > limit && isNumberChar(text.charCodeAt(start - 1))) start -= 1;
  return start < end ? start : null;
}

/** Dotted identifier chain (`col`, `t.col`, `s.t.col`) start, or null. */
function readBackwardDottedRefStart(text: string, end: number, limit: number): number | null {
  let pos = end;
  let start: number | null = null;
  for (;;) {
    const seg = readBackwardIdentifier(text, pos, limit);
    if (!seg) break;
    start = seg.start;
    pos = seg.start;
    let dotPos = pos;
    while (dotPos > limit && isWhitespaceChar(text.charCodeAt(dotPos - 1))) dotPos -= 1;
    if (dotPos === limit || text.charCodeAt(dotPos - 1) !== 0x2e /* . */) break;
    pos = dotPos - 1;
    while (pos > limit && isWhitespaceChar(text.charCodeAt(pos - 1))) pos -= 1;
  }
  return start;
}

/**
 * `end` points just past a `)`. Scan back to the matching `(`, skipping string
 * literals, then include a leading function-name identifier (if any). Returns
 * the start offset of the call (function name, or the `(` for a bare group),
 * or null when the parens are unbalanced.
 */
function readBackwardCallStart(text: string, end: number, limit: number): number | null {
  let depth = 0;
  let inStr: number | null = null;
  let openIdx = -1;
  for (let i = end - 1; i >= limit; i -= 1) {
    const ch = text.charCodeAt(i);
    if (inStr !== null) {
      if (ch === inStr) inStr = null;
      continue;
    }
    if (ch === 0x27 /* ' */ || ch === 0x22 /* " */ || ch === 0x60 /* ` */) {
      inStr = ch;
      continue;
    }
    if (ch === 0x29 /* ) */) depth += 1;
    else if (ch === 0x28 /* ( */) {
      depth -= 1;
      if (depth === 0) {
        openIdx = i;
        break;
      }
    }
  }
  if (openIdx < 0) return null;
  let i = openIdx;
  while (i > limit && isWhitespaceChar(text.charCodeAt(i - 1))) i -= 1;
  const name = readBackwardIdentifier(text, i, limit);
  return name ? name.start : openIdx;
}
